import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { configurationApi, type ConfigurationDashboard } from '../../api/configuration';

const NAV = [
  { to: '/configuration/settings', label: 'Settings' },
  { to: '/configuration/feature-flags', label: 'Feature Flags' },
  { to: '/configuration/secrets', label: 'Secret Vault' },
  { to: '/configuration/localization', label: 'Localization' },
  { to: '/configuration/preferences', label: 'Preferences' },
  { to: '/configuration/history', label: 'History & Diff' },
  { to: '/configuration/import-export', label: 'Import / Export' },
];

const MARKETPLACE_INTEGRATIONS = [
  { to: '/amazon/connect/settings', label: 'Amazon SP-API' },
  { to: '/amazon/system-explorer', label: 'System Explorer (DATAFLOW)' },
];

export function ConfigurationHubPage() {
  const [dashboard, setDashboard] = useState<ConfigurationDashboard | null>(null);
  const [error, setError] = useState('');
  const [reloadStatus, setReloadStatus] = useState('');

  useEffect(() => {
    void configurationApi.dashboard().then(setDashboard).catch((e) => setError(String(e)));
  }, []);

  const stats = dashboard
    ? [
        { label: 'Config items', value: dashboard.total_items },
        { label: 'Feature flags', value: dashboard.total_flags },
        { label: 'Enabled flags', value: dashboard.enabled_flags },
        { label: 'Secrets', value: dashboard.total_secrets },
        { label: 'Published version', value: dashboard.published_version },
      ]
    : [];

  async function handleReload() {
    try {
      await configurationApi.runtimeReload();
      setReloadStatus('Runtime configuration reloaded.');
    } catch (e) {
      setReloadStatus(String(e));
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Configuration Platform</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Enterprise configuration, feature flags, tenant preferences and dynamic settings.
      </p>
      <nav className="mt-4 flex flex-wrap gap-3 text-sm">
        {NAV.map((l) => (
          <Link key={l.to} to={l.to} className="text-slate-700 hover:underline dark:text-slate-300">{l.label}</Link>
        ))}
      </nav>
      <section className="mt-6 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
        <h2 className="text-lg font-medium">Marketplace Integrations</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Configure OAuth applications and marketplace credentials.</p>
        <nav className="mt-3 flex flex-wrap gap-3 text-sm">
          {MARKETPLACE_INTEGRATIONS.map((l) => (
            <Link key={l.to} to={l.to} className="font-medium text-brand-700 hover:underline">{l.label}</Link>
          ))}
        </nav>
      </section>
      <div className="mt-4">
        <button type="button" onClick={() => void handleReload()} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
          Runtime Reload
        </button>
        {reloadStatus && <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{reloadStatus}</p>}
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-sm text-slate-500">{s.label}</p>
            <p className="mt-1 text-2xl font-semibold">{String(s.value ?? 0)}</p>
          </div>
        ))}
      </div>
      {dashboard?.recent_activity && dashboard.recent_activity.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium">Recent activity</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {dashboard.recent_activity.map((a) => (
              <li key={a.id} className="rounded border px-3 py-2 dark:border-slate-700">
                <span className="font-medium">{a.action}</span> — {a.resource}: {a.details}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
